'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import React from 'react';
import { EnvelopeOpenIcon, WarningCircleIcon, ArchiveIcon } from '@phosphor-icons/react';

/**
 * EmptyState — shared placeholder for the message-list and reading-pane
 * regions when there's nothing to render (no selection, no results,
 * not-yet-ingested mailbox, etc.).
 *
 * The variants here are intentionally limited to the cases the viewer can
 * actually hit; new kinds should be added explicitly rather than freeforming
 * a "title + caption" prop pair.
 */

type EmptyStateKind =
  | 'select-a-message'
  | 'no-results'
  | 'empty-mailbox'
  | 'not-ingested'
  | 'no-body'
  | 'no-s3key'
  | 'error';

interface EmptyStateProps {
  kind: EmptyStateKind;
  /** Optional override for the heading text. */
  title?: string;
  /** Optional override for the caption text. */
  detail?: string;
  /** Optional action button rendered below the caption. */
  action?: React.ReactNode;
}

function defaultsFor(kind: EmptyStateKind): { title: string; detail: string; icon: React.ReactNode } {
  const t = useTimePilotT();
  switch (kind) {
    case 'select-a-message':
      return {
        title: t("viewers.selectAMessageToRead"),
        detail: 'Pick any message from the list to view its contents here.',
        icon: <EnvelopeOpenIcon size={48} className="text-gray-300" />,
      };
    case 'no-results':
      return {
        title: t("viewers.noMessagesMatchTheCurrentFilter"),
        detail: 'Clear the date range or sort to see all messages.',
        icon: <ArchiveIcon size={48} className="text-gray-300" />,
      };
    case 'empty-mailbox':
      return {
        title: t("viewers.thisMailboxIsEmpty"),
        detail: 'The loader indexed this file but found no messages inside it.',
        icon: <ArchiveIcon size={48} className="text-gray-300" />,
      };
    case 'not-ingested':
      return {
        title: t("viewers.thisMailboxHasnTBeenIndexedYet"),
        detail:
          'The data loader needs to ingest this file before its contents can be viewed.',
        icon: <WarningCircleIcon size={48} className="text-amber-400" />,
      };
    case 'no-body':
      return {
        title: t("viewers.thisMessageHasNoBodyContent"),
        detail: 'Headers and attachments above; nothing else to display.',
        icon: <EnvelopeOpenIcon size={48} className="text-gray-300" />,
      };
    case 'no-s3key':
      return {
        title: t("viewers.noSourceFileSpecified"),
        detail: 'Add an "s3Key" query parameter to the URL to open a mailbox.',
        icon: <WarningCircleIcon size={48} className="text-amber-400" />,
      };
    case 'error':
      return {
        title: t("viewers.somethingWentWrong"),
        detail: 'Reload the page or try again in a moment.',
        icon: <WarningCircleIcon size={48} className="text-red-400" />,
      };
  }
}

export function EmptyState({ kind, title, detail, action }: EmptyStateProps) {
  const defaults = defaultsFor(kind);
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
      {defaults.icon}
      <div className="text-base font-medium text-gray-700">{title ?? defaults.title}</div>
      <div className="max-w-md text-sm text-gray-500">{detail ?? defaults.detail}</div>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
